/**
 * Cart-level coupon helpers: safe currency parsing, product ID collection,
 * subtotal, and final payable amount.
 */

export const COUPON_DEBUG = true;

export const logCouponDebug = (label, payload) => {
  if (!COUPON_DEBUG) return;
  console.log(`[coupon-debug] ${label}`, payload);
};

/** Round to cents without floating-point drift. */
export const roundMoney = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

/**
 * Parse API/UI money values that may include thousands separators.
 * "4,485.00" → 4485, never parseFloat("4,485.00") which yields 4.
 */
export const parseNumericAmount = (value) => {
  if (value == null || value === "") return NaN;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }

  const raw = String(value).trim();
  if (!raw || raw === "undefined" || raw === "null") return NaN;

  const wrappedNegative = /^\(.*\)$/.test(raw);
  const leadingNegative = raw.trim().startsWith("-");
  let str = raw.replace(/[()\s]/g, "").replace(/^[+-]/, "");
  str = str.replace(/[^0-9.,-]/g, "");
  if (!str) return NaN;

  const lastComma = str.lastIndexOf(",");
  const lastDot = str.lastIndexOf(".");

  if (lastComma !== -1 && lastDot !== -1) {
    if (lastDot > lastComma) {
      str = str.replace(/,/g, "");
    } else {
      str = str.replace(/\./g, "").replace(",", ".");
    }
  } else if (lastComma !== -1) {
    const parts = str.split(",");
    if (parts.length === 2 && parts[1].length > 0 && parts[1].length <= 2) {
      str = `${parts[0]}.${parts[1]}`;
    } else {
      str = str.replace(/,/g, "");
    }
  }

  const num = Number(str);
  if (!Number.isFinite(num)) return NaN;
  return wrappedNegative || leadingNegative ? -Math.abs(num) : num;
};

/** Display currency with thousands separators (e.g. 4,485.00). */
export const formatMoney = (value) => {
  const num = parseNumericAmount(value);
  if (!Number.isFinite(num)) return "0.00";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Plain 2-decimal string for APIs / stored numeric state. */
export const formatMoneyPlain = (value) => {
  const num = parseNumericAmount(value);
  if (!Number.isFinite(num)) return "0.00";
  return roundMoney(num).toFixed(2);
};

export const hasPercentDiscount = (pct) => {
  const value = parseNumericAmount(pct);
  return Number.isFinite(value) && value > 0;
};

export const splitProductIds = (raw) => {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) {
    return raw.flatMap((part) => splitProductIds(part));
  }
  return String(raw)
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id && id !== "undefined" && id !== "null");
};

export const resolveProductId = (product) => {
  if (product == null) return "";
  const candidates = [
    product.product_id,
    product.requested_product_id,
    product.productId,
    product.id,
    product.product_sku,
    product.sku,
  ];
  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    const id = String(candidate).trim();
    if (id && id !== "undefined" && id !== "null") return id;
  }
  return "";
};

export const getLineQty = (product) =>
  Math.max(1, parseInt(product?.quantity, 10) || 1);

/**
 * Comma-separated product IDs for every cart line, repeated by quantity.
 * Never unique/drop IDs — 8 cart products must produce 8 IDs (qty 1 each).
 */
export const getSelectedProductIdString = (products = []) => {
  if (!Array.isArray(products) || products.length === 0) return "";
  return products
    .flatMap((product) => {
      const id = resolveProductId(product);
      if (!id) {
        logCouponDebug("missing-product-id", { product });
        return [];
      }
      return Array.from({ length: getLineQty(product) }, () => id);
    })
    .join(",");
};

export const countProductIdsInString = (idString) =>
  splitProductIds(idString).length;

export const getProductUnitPrice = (product) => {
  const raw =
    product?.product_price ??
    product?.price ??
    product?.m_price ??
    product?.original_price ??
    0;
  const value = parseNumericAmount(raw);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

export const getLineItemOriginalDollars = (product) =>
  roundMoney(getProductUnitPrice(product) * getLineQty(product));

/** Subtotal of ALL cart products (unit price × quantity). */
export const getProductsSubtotalDollars = (products = []) => {
  if (!Array.isArray(products) || products.length === 0) return 0;
  const total = products.reduce(
    (sum, product) => sum + getLineItemOriginalDollars(product),
    0,
  );
  return roundMoney(total);
};

export const resolveCheckoutSubtotal = (products, fallbackPrice) => {
  const fromProducts = getProductsSubtotalDollars(products);
  if (fromProducts > 0) return fromProducts;
  const fallback = parseNumericAmount(fallbackPrice);
  return Number.isFinite(fallback) && fallback > 0 ? roundMoney(fallback) : 0;
};

export const getCartCouponFingerprint = (products = []) =>
  (Array.isArray(products) ? products : [])
    .map((product) => {
      const id = resolveProductId(product);
      return `${id}:${getLineQty(product)}:${getProductUnitPrice(product)}`;
    })
    .join("|");

export const normalizeCartProduct = (productData, requestedId, quantity = 1) => {
  const record = Array.isArray(productData) ? productData[0] : productData;
  const qty = Math.max(1, parseInt(quantity, 10) || 1);
  const requested = String(requestedId ?? "").trim();
  if (!record || typeof record !== "object") {
    return {
      product_id: requested,
      requested_product_id: requested,
      quantity: qty,
    };
  }
  return {
    ...record,
    product_id: resolveProductId(record) || requested,
    requested_product_id: requested,
    quantity: qty,
  };
};

/**
 * Cart-level coupon math for any product count.
 * subtotal = sum(price × qty)
 * discount = valid API amount, else percent of subtotal
 * finalTotal = max(0, subtotal - discount)
 */
export const computeCartCouponPricing = (products, fallbackPrice, pct, amt) => {
  const originalCartTotal = resolveCheckoutSubtotal(products, fallbackPrice);
  if (!(originalCartTotal > 0)) {
    return {
      originalCartTotal: 0,
      discountAmount: 0,
      latestPrice: 0,
      savedAmount: 0,
    };
  }

  const parsedAmount = parseNumericAmount(amt);
  const parsedPct = parseNumericAmount(pct);

  let discountAmount = 0;
  if (Number.isFinite(parsedAmount) && parsedAmount > 0) {
    discountAmount = parsedAmount;
  } else if (Number.isFinite(parsedPct) && parsedPct > 0) {
    discountAmount = (originalCartTotal * parsedPct) / 100;
  }

  discountAmount = roundMoney(
    Math.min(originalCartTotal, Math.max(0, discountAmount)),
  );
  const latestPrice = roundMoney(
    Math.max(0, originalCartTotal - discountAmount),
  );
  const savedAmount = roundMoney(
    Math.max(0, originalCartTotal - latestPrice),
  );

  return {
    originalCartTotal,
    discountAmount,
    latestPrice,
    savedAmount,
  };
};

export const getCouponSavingsDollars = (basePrice, pct, amt, products) =>
  computeCartCouponPricing(products, basePrice, pct, amt).savedAmount;

export const getDiscountedTotalDollars = (basePrice, pct, amt, products) =>
  computeCartCouponPricing(products, basePrice, pct, amt).latestPrice;
